import { Row, Col, Button } from "reactstrap";

import "./AudioUploader.scss";

import { TextInput } from "../../../components/Input";
import RadioGroup from "../../../components/Input/RadioGroup";
import { LABELS, NAMES, PLACEHOLDERS } from "../Config";

import uploadFileIcon from "../../../assets/images/app/allaya/file-upload.svg";
import modalCloseIcon from "../../../assets/images/logo/modal-close-icon.png";
import { formatFileSize } from "../../../utils/mediaUtils";
import { truncateFileName } from "../../../utils/commonUtils";

let {
    SESSION_TITLE,
    SESSIONS,
    SESSION_DESCRIPTION,
    SESSION_COMPLETION_MESSAGE,
    SESSION_UPLOAD_AUDIO,
    SESSION_SUBSCRIPTION,
    RADIO_GROUP
} = LABELS;

const SessionDetail = ({ formikInstance, sessionIndex, handleRemove, isDisabled }) => {
    const fieldPrefix = `${NAMES[SESSIONS]}[${sessionIndex}]`;

    const handleRemoveFile = (sessionIndex) => {
        const updatedSessions = [...formikInstance.values.sessions];
        updatedSessions[sessionIndex] = {
            ...updatedSessions[sessionIndex],
            sessionUploadAudio: null
        };
        formikInstance.setFieldValue("sessions", updatedSessions);
    };

    const handleAudioChange = (event, sessionIndex) => {
        const file = event.target.files[0];
        if (file) {
            const isValidType = ["m4a", "mp3", "wav"].some((type) =>
                file.name.toLowerCase().endsWith(`.${type}`)
            );
            if (!isValidType) {
                alert("Invalid file type. Only mp3, m4a, wav files are allowed.");
                return;
            }

            if (file.size > 300 * 1024 * 1024) {
                alert("File size exceeds 300MB.");
                return;
            }

            const audioURL = URL.createObjectURL(file);
            const audio = new Audio(audioURL);

            // Get audio duration once it's loaded
            audio.onloadedmetadata = () => {
                const duration = audio.duration; // Duration in seconds

                const updatedSessions = [...formikInstance.values.sessions];

                // Correctly update the specific session with the file and duration
                updatedSessions[sessionIndex] = {
                    ...updatedSessions[sessionIndex],
                    sessionUploadAudio: {
                        file: {
                            name: file.name,
                            size: formatFileSize(file.size),
                            type: file.type
                        },
                        duration,
                        exactFile: file
                    } // Save the duration along with the file
                };

                formikInstance.setFieldValue("sessions", updatedSessions);
            };
        }
    };

    return (
        <div className="session-detail">
            <Row className="mb-2">
                {/* Session Title */}

                <Col md={6}>
                    <TextInput
                        disabled={isDisabled}
                        placeholder={PLACEHOLDERS[SESSION_TITLE]}
                        label={SESSION_TITLE}
                        name={`${fieldPrefix}.${NAMES[SESSION_TITLE]}`} // Dynamically using fieldPrefix
                        onChange={formikInstance.handleChange}
                        onBlur={formikInstance.handleBlur}
                        value={formikInstance.values.sessions?.[sessionIndex]?.sessionTitle || ""} // Correctly accessing sessionTitle dynamically
                    />
                    {formikInstance.errors.sessions?.[sessionIndex]?.sessionTitle && (
                        <span className="error">
                            {formikInstance.errors.sessions?.[sessionIndex]?.sessionTitle}
                        </span>
                    )}
                </Col>

                <Col md={6}>
                    {/* Audio Upload UI */}
                    <label className="input-label">{SESSION_UPLOAD_AUDIO}</label>
                    <div className="Audio-uploader">
                        {!formikInstance.values.sessions[sessionIndex]?.sessionUploadAudio ? (
                            <div className="Audio-upload-box">
                                <div
                                    className="Audio-upload-button"
                                    onClick={() =>
                                        document
                                            .getElementById(`audio-upload-input-${sessionIndex}`)
                                            .click()
                                    }
                                >
                                    <img
                                        src={uploadFileIcon}
                                        alt="Upload Icon"
                                        className="upload-icon"
                                    />
                                    Browse file
                                </div>
                                <span className="Audio-upload-helper">
                                    Support format: mp3, m4a, wav Max file size: 300MB
                                </span>
                            </div>
                        ) : (
                            <div className="Audio-upload-box">
                                <input
                                    type="text"
                                    className="Audio-file-name"
                                    value={
                                        formikInstance.values.sessions[sessionIndex]
                                            ?.sessionUploadAudio?.audioMedia?.name
                                            ? truncateFileName(
                                                  formikInstance.values.sessions[sessionIndex]
                                                      ?.sessionUploadAudio?.audioMedia?.name,
                                                  10
                                              )
                                            : truncateFileName(
                                                  formikInstance.values.sessions[sessionIndex]
                                                      ?.sessionUploadAudio?.file?.name,
                                                  10
                                              )
                                    }
                                    readOnly
                                />
                                <button
                                    className="Audio-remove-button"
                                    onClick={() => handleRemoveFile(sessionIndex)}
                                    type="button"
                                    aria-label="Remove file"
                                >
                                    <img src={modalCloseIcon} alt="Remove File" />
                                </button>
                            </div>
                        )}
                    </div>
                    {/* Hidden File Input */}
                    <input
                        type="file"
                        id={`audio-upload-input-${sessionIndex}`}
                        accept=".m4a, .mp3, .wav"
                        style={{ display: "none" }}
                        onChange={(e) => handleAudioChange(e, sessionIndex)}
                    />

                    {formikInstance.errors.sessions?.[sessionIndex]?.sessionUploadAudio && (
                        <span className="error">
                            {formikInstance.errors.sessions?.[sessionIndex]?.sessionUploadAudio}
                        </span>
                    )}
                </Col>
            </Row>

            {/* Session Description */}
            <Row className="mb-2">
                <Col md={12}>
                    <TextInput
                        disabled={isDisabled}
                        type="textarea"
                        placeholder={PLACEHOLDERS[SESSION_DESCRIPTION]}
                        label={SESSION_DESCRIPTION}
                        name={`${fieldPrefix}.${NAMES[SESSION_DESCRIPTION]}`} // Dynamically using fieldPrefix
                        onChange={formikInstance.handleChange}
                        onBlur={formikInstance.handleBlur}
                        value={
                            formikInstance.values.sessions[sessionIndex]?.sessionDescription || ""
                        }
                    />

                    {formikInstance.errors.sessions?.[sessionIndex]?.sessionDescription && (
                        <span className="error">
                            {formikInstance.errors.sessions?.[sessionIndex]?.sessionDescription}
                        </span>
                    )}
                </Col>
            </Row>
            <Row className="mb-2">
                {/* Session Success Message */}
                <Col md={12}>
                    <TextInput
                        disabled={isDisabled}
                        type="textarea"
                        placeholder={PLACEHOLDERS[SESSION_COMPLETION_MESSAGE]}
                        label={SESSION_COMPLETION_MESSAGE}
                        name={`${fieldPrefix}.${NAMES[SESSION_COMPLETION_MESSAGE]}`} // Dynamically using fieldPrefix
                        onChange={formikInstance.handleChange}
                        onBlur={formikInstance.handleBlur}
                        value={
                            formikInstance.values.sessions[sessionIndex]
                                ?.sessionCompletionmessage || ""
                        }
                    />

                    {formikInstance.errors.sessions?.[sessionIndex]?.sessionCompletionmessage && (
                        <span className="error">
                            {
                                formikInstance.errors.sessions?.[sessionIndex]
                                    ?.sessionCompletionmessage
                            }
                        </span>
                    )}
                </Col>
            </Row>
            <Row className="my-4">
                <label className="input-label">{SESSION_SUBSCRIPTION}</label>

                <RadioGroup
                    field={`${fieldPrefix}.${NAMES[RADIO_GROUP]}`}
                    setFieldValue={formikInstance.setFieldValue}
                    value={formikInstance.values.sessions[sessionIndex]?.radioGroup || ""}
                />
                {formikInstance.errors.sessions?.[sessionIndex]?.radioGroup && (
                    <span className="error">
                        {formikInstance.errors.sessions?.[sessionIndex]?.radioGroup}
                    </span>
                )}
            </Row>
            {sessionIndex > 0 && (
                <Row className="mt-3">
                    <Col md={12}>
                        <Button
                            onClick={() => handleRemove(sessionIndex)}
                            color="link"
                            className="text-danger add-more-session"
                        >
                            Remove Session
                        </Button>
                    </Col>
                </Row>
            )}
        </div>
    );
};

export default SessionDetail;
